/**
 * Benutzerverwaltung.
 *
 * Benutzer werden nie geloescht, sondern gesperrt: Ihre Kennung steht in
 * Belegen und im Aenderungsprotokoll, und diese Spur muss erhalten
 * bleiben.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatTimestamp, permissionSectionsFor, type Permission } from '@erp/shared';

import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import { Alert, Badge, Dialog, Field, LoadingBlock, TextInput } from '../components/ui';
import { GroupsPage, type Group } from './GroupsPage';

type ManagedUser = {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  legacyName: string | null;
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  groups: { id: string; name: string; active: boolean }[];
  permissionCount: number;
  overrideCount: number;
};

/** Ergebnis von Anlegen/Zurücksetzen: entweder gemailt oder einmalig angezeigt. */
type StartPasswordResult = {
  username: string;
  password: string | null;
  mailedTo: string | null;
  mailError: string | null;
};

type EffectiveEntry = {
  permission: Permission;
  granted: boolean;
  origin: 'group' | 'user_allow' | 'user_deny' | 'none';
  groups: string[];
  note?: string | null;
};

export function UsersPage() {
  const [tab, setTab] = useState<'users' | 'groups'>('users');

  return (
    <>
      <PageHeader
        title="Benutzer und Rechte"
        subtitle="Jeder Mitarbeiter braucht ein eigenes Konto — nur so bleibt im Protokoll nachvollziehbar, wer was geändert hat."
      />

      <div className="row" style={{ marginBottom: 16, gap: 6 }}>
        <button
          type="button"
          className={`btn btn-sm${tab === 'users' ? ' btn-primary' : ''}`}
          onClick={() => setTab('users')}
        >
          Benutzer
        </button>
        <button
          type="button"
          className={`btn btn-sm${tab === 'groups' ? ' btn-primary' : ''}`}
          onClick={() => setTab('groups')}
        >
          Gruppen
        </button>
      </div>

      {tab === 'users' ? <UserList /> : <GroupsPage />}
    </>
  );
}

function UserList() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [permissionsFor, setPermissionsFor] = useState<ManagedUser | null>(null);
  const [newPassword, setNewPassword] = useState<StartPasswordResult | null>(null);
  const [editing, setEditing] = useState<ManagedUser | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: ManagedUser[] }>('/api/users'),
  });

  const update = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Record<string, unknown> }) =>
      api.patch(`/api/users/${id}`, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Gespeichert.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Änderung fehlgeschlagen.'),
  });

  const resetPassword = useMutation({
    mutationFn: (user: ManagedUser) =>
      api
        .post<{ initialPassword: string | null; mailedTo: string | null; mailError: string | null }>(
          `/api/users/${user.id}/reset-password`,
        )
        .then((result) => ({
          username: user.username,
          password: result.initialPassword,
          mailedTo: result.mailedTo,
          mailError: result.mailError,
        })),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setNewPassword(result);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Zurücksetzen fehlgeschlagen.'),
  });

  if (isLoading || !data) return <LoadingBlock />;

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h2>{data.users.length} Benutzer</h2>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            Benutzer anlegen
          </button>
        </div>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>Gruppen</th>
                <th className="numeric">Rechte</th>
                <th className="shrink">Letzte Anmeldung</th>
                <th className="shrink">Status</th>
                <th className="shrink"></th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{user.displayName}</div>
                    <div className="faint mono">
                      {user.username}
                      {user.legacyName ? ` · T4: ${user.legacyName}` : ''}
                    </div>
                    {user.email ? <div className="faint">{user.email}</div> : null}
                  </td>
                  <td>
                    {user.groups.length === 0 ? (
                      <span style={{ color: 'var(--warning)' }}>keine Gruppe</span>
                    ) : (
                      <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                        {user.groups.map((group) => (
                          <Badge key={group.id} kind={group.active ? 'accent' : 'warning'}>
                            {group.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="numeric">
                    {user.permissionCount}
                    {user.overrideCount > 0 ? (
                      <div className="faint" style={{ fontSize: 11.5 }}>
                        {user.overrideCount} abweichend
                      </div>
                    ) : null}
                  </td>
                  <td className="muted">
                    {user.lastLoginAt ? formatTimestamp(user.lastLoginAt) : 'noch nie'}
                  </td>
                  <td>
                    {!user.active ? (
                      <Badge kind="warning">Gesperrt</Badge>
                    ) : user.mustChangePassword ? (
                      <Badge kind="accent">Passwortwechsel offen</Badge>
                    ) : (
                      <Badge kind="success">Aktiv</Badge>
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6 }}>
                      <button type="button" className="btn btn-sm" onClick={() => setEditing(user)}>
                        Bearbeiten
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setPermissionsFor(user)}
                      >
                        Rechte
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => resetPassword.mutate(user)}
                      >
                        Passwort
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() =>
                          update.mutate({ id: user.id, changes: { active: !user.active } })
                        }
                      >
                        {user.active ? 'Sperren' : 'Entsperren'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate ? (
        <CreateUserDialog
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            setShowCreate(false);
            if (result) setNewPassword(result);
          }}
        />
      ) : null}

      {permissionsFor ? (
        <UserPermissionsDialog user={permissionsFor} onClose={() => setPermissionsFor(null)} />
      ) : null}

      {editing ? (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSave={(changes) => {
            update.mutate({ id: editing.id, changes });
            setEditing(null);
          }}
        />
      ) : null}

      {newPassword ? (
        <Dialog
          title="Startpasswort"
          onClose={() => setNewPassword(null)}
          footer={
            <button type="button" className="btn btn-primary" onClick={() => setNewPassword(null)}>
              Schließen
            </button>
          }
        >
          {newPassword.mailedTo ? (
            <>
              <p style={{ marginTop: 0 }}>
                Das Startpasswort für <strong>{newPassword.username}</strong> wurde an{' '}
                <strong>{newPassword.mailedTo}</strong> gemailt.
              </p>
              <Alert kind="info">
                Es gilt nur für die erste Anmeldung und muss dort geändert werden. Hier wird es
                nicht angezeigt.
              </Alert>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                Für <strong>{newPassword.username}</strong> wurde folgendes Startpasswort erzeugt:
              </p>
              <p
                className="mono"
                style={{
                  padding: '10px 14px',
                  background: 'var(--bg-sunken)',
                  borderRadius: 6,
                  fontSize: 16,
                  letterSpacing: '0.05em',
                }}
              >
                {newPassword.password}
              </p>
              {newPassword.mailError ? (
                <Alert kind="error">
                  Der Mailversand ist fehlgeschlagen ({newPassword.mailError}). Geben Sie das
                  Passwort bitte auf anderem Weg weiter.
                </Alert>
              ) : null}
              <Alert kind="warning">
                Notieren Sie es jetzt — es wird nirgends gespeichert und lässt sich nicht erneut
                anzeigen. Bei der ersten Anmeldung muss es geändert werden. Mit hinterlegter
                E-Mail-Adresse und eingerichtetem Mailversand ginge es direkt an den Benutzer.
              </Alert>
            </>
          )}
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * Rechte eines Benutzers: Gruppen und Abweichungen.
 *
 * Die Matrix zeigt für jedes Recht drei Zustände - geerbt, ausdrücklich
 * erlaubt, ausdrücklich verweigert - und daneben, woher der aktuelle
 * Zustand kommt. Ohne diese Herkunft ist die Frage „warum darf der das?"
 * bei mehreren Gruppen nicht zu beantworten.
 */
function UserPermissionsDialog({ user, onClose }: { user: ManagedUser; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { modules } = useAuth();

  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(
    new Set(user.groups.map((group) => group.id)),
  );

  const { data: groups } = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<{ groups: Group[] }>('/api/groups'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['user-permissions', user.id],
    queryFn: () =>
      api.get<{
        groups: { id: string; name: string; active: boolean }[];
        effective: EffectiveEntry[];
      }>(`/api/users/${user.id}/permissions`),
  });

  const saveGroups = useMutation({
    mutationFn: () => api.put(`/api/users/${user.id}/groups`, { groupIds: [...selectedGroups] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions', user.id] });
      toast.success('Gruppen gespeichert. Der Benutzer wurde abgemeldet.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'),
  });

  const setOverride = useMutation({
    mutationFn: ({ permission, effect }: { permission: Permission; effect: 'allow' | 'deny' | null }) =>
      api.put(`/api/users/${user.id}/permissions/${permission}`, { effect, note: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions', user.id] });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Die Änderung wurde abgelehnt.'),
  });

  const byPermission = new Map((data?.effective ?? []).map((entry) => [entry.permission, entry]));

  return (
    <Dialog
      title={`Rechte · ${user.displayName}`}
      wide
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Schließen
        </button>
      }
    >
      {isLoading ? <LoadingBlock /> : null}

      <fieldset>
        <legend>Gruppen</legend>
        <div className="stack" style={{ gap: 6 }}>
          {(groups?.groups ?? []).map((group) => (
            <label key={group.id} className="checkbox-row" style={{ alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={selectedGroups.has(group.id)}
                style={{ marginTop: 3 }}
                onChange={(event) => {
                  setSelectedGroups((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(group.id);
                    else next.delete(group.id);
                    return next;
                  });
                }}
              />
              <span>
                {group.name}
                {!group.active ? <Badge kind="warning"> inaktiv</Badge> : null}
                <span className="faint" style={{ display: 'block', fontSize: 12 }}>
                  {group.permissions.length} Rechte
                  {group.description ? ` · ${group.description}` : ''}
                </span>
              </span>
            </label>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-sm btn-primary"
          style={{ marginTop: 10 }}
          disabled={saveGroups.isPending}
          onClick={() => saveGroups.mutate()}
        >
          Gruppen übernehmen
        </button>
      </fieldset>

      <fieldset style={{ marginBottom: 0 }}>
        <legend>Einzelrechte</legend>

        <Alert kind="info">
          „Geerbt" richtet sich nach den Gruppen. Eine ausdrückliche Erlaubnis oder Verweigerung
          hat Vorrang — auch gegen eine Gruppe, die das Recht mitbringt.
        </Alert>

        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Recht</th>
                <th className="shrink">Aus Gruppe</th>
                <th className="shrink">Einstellung</th>
                <th className="shrink">Wirksam</th>
              </tr>
            </thead>
            <tbody>
              {permissionSectionsFor(modules).map((section) =>
                section.permissions.map((entry, index) => {
                  const effective = byPermission.get(entry.key);
                  const override =
                    effective?.origin === 'user_allow'
                      ? 'allow'
                      : effective?.origin === 'user_deny'
                        ? 'deny'
                        : '';

                  return (
                    <tr key={entry.key}>
                      <td>
                        {index === 0 ? (
                          <div className="faint" style={{ fontSize: 11.5, marginBottom: 2 }}>
                            {section.title}
                          </div>
                        ) : null}
                        {entry.label}
                        <div className="faint" style={{ fontSize: 11.5 }}>
                          {entry.description}
                        </div>
                      </td>
                      <td className="muted" style={{ fontSize: 12.5 }}>
                        {effective?.groups.length ? effective.groups.join(', ') : '—'}
                      </td>
                      <td>
                        <select
                          className="select input"
                          style={{ width: 150 }}
                          value={override}
                          disabled={setOverride.isPending}
                          onChange={(event) =>
                            setOverride.mutate({
                              permission: entry.key,
                              effect: (event.target.value || null) as 'allow' | 'deny' | null,
                            })
                          }
                        >
                          <option value="">Geerbt</option>
                          <option value="allow">Ausdrücklich erlauben</option>
                          <option value="deny">Ausdrücklich verweigern</option>
                        </select>
                      </td>
                      <td>
                        {effective?.granted ? (
                          <Badge kind="success">ja</Badge>
                        ) : (
                          <Badge kind={effective?.origin === 'user_deny' ? 'danger' : 'neutral'}>
                            nein
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>
      </fieldset>
    </Dialog>
  );
}

function CreateUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (result: StartPasswordResult | null) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [legacyName, setLegacyName] = useState('');
  const [groupIds, setGroupIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: groups } = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<{ groups: Group[] }>('/api/groups'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ initialPassword: string | null; mailedTo: string | null; mailError: string | null }>('/api/users', {
        username,
        displayName,
        email: email || null,
        legacyName: legacyName || null,
        groupIds: [...groupIds],
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Der Benutzer wurde angelegt.');
      onCreated(
        result.initialPassword || result.mailedTo
          ? {
              username,
              password: result.initialPassword,
              mailedTo: result.mailedTo,
              mailError: result.mailError,
            }
          : null,
      );
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        if (Object.keys(error.fieldErrors).length === 0) toast.error(error.message);
      } else {
        toast.error('Der Benutzer konnte nicht angelegt werden.');
      }
    },
  });

  return (
    <Dialog
      title="Benutzer anlegen"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={create.isPending || groupIds.size === 0}
            onClick={() => {
              setErrors({});
              create.mutate();
            }}
          >
            Anlegen
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <Field
          label="Benutzername"
          required
          error={errors.username}
          hint="Kleinbuchstaben, Ziffern, Punkt, Bindestrich"
        >
          <TextInput
            value={username}
            onChange={(value) => setUsername(value.toLowerCase())}
            invalid={Boolean(errors.username)}
            autoFocus
          />
        </Field>
        <Field label="Anzeigename" required error={errors.displayName}>
          <TextInput
            value={displayName}
            onChange={setDisplayName}
            invalid={Boolean(errors.displayName)}
          />
        </Field>
        <Field label="E-Mail" error={errors.email}>
          <TextInput value={email} onChange={setEmail} type="email" />
        </Field>
        <Field
          label="Name im Altsystem (T4)"
          error={errors.legacyName}
          hint="Benutzername oder Kürzel aus T4 - das Portal ordnet damit „meine Kunden“ zu"
        >
          <TextInput value={legacyName} onChange={setLegacyName} />
        </Field>

        <Field
          label="Gruppen"
          required
          error={errors.groupIds}
          hint="Mindestens eine — ohne Gruppe hätte der Benutzer kein einziges Recht"
        >
          <div className="stack" style={{ gap: 4, paddingTop: 4 }}>
            {(groups?.groups ?? [])
              .filter((group) => group.active)
              .map((group) => (
                <label key={group.id} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={groupIds.has(group.id)}
                    onChange={(event) => {
                      setGroupIds((current) => {
                        const next = new Set(current);
                        if (event.target.checked) next.add(group.id);
                        else next.delete(group.id);
                        return next;
                      });
                    }}
                  />
                  {group.name}
                  <span className="faint" style={{ fontSize: 12 }}>
                    ({group.permissions.length} Rechte)
                  </span>
                </label>
              ))}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}

/** Anzeigename, E-Mail und Altsystem-Name eines Benutzers ändern. */
function EditUserDialog({
  user,
  onClose,
  onSave,
}: {
  user: ManagedUser;
  onClose: () => void;
  onSave: (changes: { displayName: string; email: string | null; legacyName: string | null }) => void;
}) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [email, setEmail] = useState(user.email ?? '');
  const [legacyName, setLegacyName] = useState(user.legacyName ?? '');

  return (
    <Dialog
      title={`Benutzer ${user.username} bearbeiten`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!displayName.trim()}
            onClick={() =>
              onSave({
                displayName: displayName.trim(),
                email: email.trim() || null,
                legacyName: legacyName.trim() || null,
              })
            }
          >
            Speichern
          </button>
        </>
      }
    >
      <Field label="Anzeigename" required>
        <TextInput value={displayName} onChange={setDisplayName} autoFocus />
      </Field>
      <Field label="E-Mail" hint="Für Benachrichtigungen, Startpasswort und Antwortadresse beim Belegversand">
        <TextInput value={email} onChange={setEmail} type="email" />
      </Field>
      <Field
        label="Name im Altsystem (T4)"
        hint="Benutzername oder Kürzel aus T4 - das Portal ordnet damit „meine Kunden“ zu"
      >
        <TextInput value={legacyName} onChange={setLegacyName} />
      </Field>
    </Dialog>
  );
}
